import { useEffect, useState, type ReactNode } from 'react'

/**
 * Le rendu mathématique, chargé à la demande — ADR-07, D-29.
 *
 * **KaTeX ne doit jamais entrer dans le point d'entrée.** Il pèse à lui seul,
 * polices comprises, davantage que tout le reste de l'application ; et la très
 * grande majorité des items sont du texte. Le charger d'office ferait payer à
 * tous les candidats ce dont quelques-uns ont besoin — exactement ce que PR-2
 * interdit. `pnpm budget` vérifie qu'il n'y est pas, parce que la relecture ne
 * le verrait pas : un `import` distrait en tête d'un composant suffirait. Depuis
 * §153, la même porte vérifie que le morceau différé emporte bien SA FEUILLE DE
 * STYLE — sans elle, KaTeX rend les fractions à l'envers sans que rien ne lève
 * la moindre erreur.
 *
 * Le serveur dit à l'avance si une carte porte des formules (`has_math`) : on
 * ne déclenche donc le chargement que pour celles-là, et jamais « au cas où ».
 * La ligne-item y ajoute ce qu'elle a réellement à l'écran (§153) : le serveur
 * ne peut pas connaître le dévoilement d'une évaluation notée, qui n'existe pas
 * encore au moment où la carte part.
 */

type Katex = { renderToString: (latex: string, options: object) => string }

let promesse: Promise<Katex> | null = null

/**
 * Charge KaTeX — **et sa feuille de style** — une seule fois pour la session.
 *
 * **Le CSS n'est pas un ornement : c'est la mise en page** — [DECISIONS §153].
 * `renderToString` produit un arbre de `span` dont tout le sens tient au style :
 * une fraction est une pile de trois boîtes positionnées, un exposant est une
 * boîte décalée. Sans la feuille, le navigateur affiche cet arbre en flux, dans
 * l'ordre du DOM — et cet ordre n'est pas l'ordre de lecture. KaTeX empilant ses
 * boîtes du bas vers le haut, `$\dfrac{m}{M}$` sortait « Mm », dénominateur
 * d'abord, et `$\frac{56}{6{,}022 \times 10^{23}}$` sortait
 * « 6,022 × 102356 ». La panne se lisait comme une faute de contenu : on
 * cherchait le défaut dans le corpus, où il n'était pas.
 *
 * **Dans le même `import()` que la bibliothèque, et pour la même raison.** En
 * tête de l'application, cette feuille serait payée par tous les candidats, y
 * compris ceux qui ne verront jamais une formule (PR-2). Ici, elle arrive avec
 * KaTeX ou pas du tout : le morceau différé porte les deux.
 */
export function chargerKatex(): Promise<Katex> {
  promesse ??= Promise.all([import('katex'), import('katex/dist/katex.min.css')]).then(
    ([module]) => module.default as unknown as Katex,
  )

  return promesse
}

/**
 * Le texte porte-t-il une formule ? — le critère du serveur, à la lettre.
 *
 * **`texte.includes('$')` n'était pas ce critère.** Un prix en dollars échappé
 * `\$`, un `` `$` `` cité entre accents graves dans un énoncé d'informatique :
 * ni l'un ni l'autre n'est une formule, et `RichText::hasMath()` les écarte
 * déjà côté PHP. Deux définitions de « porte des mathématiques » finiraient par
 * diverger, et le désaccord se paierait en KaTeX chargé pour rien — ou, ce qui
 * est pire, pas chargé du tout.
 */
export function porteDesFormules(...textes: (string | null | undefined)[]): boolean {
  return textes.some((texte) => {
    if (texte === null || texte === undefined) return false

    return /\$[^$]+\$/.test(texte.replace(/\\\$/g, '').replace(/`[^`]*`/g, ''))
  })
}

/**
 * Rend les formules d'une carte, quand elle en a.
 *
 * Renvoie `undefined` tant que KaTeX n'est pas là : le texte s'affiche alors
 * avec ses délimiteurs. C'est laid une fraction de seconde, et c'est
 * infiniment préférable à une carte vide — le candidat qui défile ne doit
 * jamais attendre un paquet.
 */
export function useMath(actif: boolean): ((latex: string, bloc: boolean) => ReactNode) | undefined {
  const [katex, setKatex] = useState<Katex | null>(null)

  useEffect(() => {
    if (!actif || katex !== null) return

    let vivant = true

    void chargerKatex().then((charge) => {
      if (vivant) setKatex(charge)
    })

    return () => {
      vivant = false
    }
  }, [actif, katex])

  if (!actif || katex === null) return undefined

  return (latex, bloc) => <Formule katex={katex} latex={latex} bloc={bloc} />
}

function Formule({ katex, latex, bloc }: { katex: Katex; latex: string; bloc: boolean }) {
  let html: string

  try {
    html = katex.renderToString(latex, { displayMode: bloc, throwOnError: false, output: 'html' })
  } catch {
    // Une formule mal écrite ne fait pas tomber la carte : le candidat voit le
    // LaTeX brut, et l'éditeur le corrigera. `throwOnError: false` couvre déjà
    // le cas ; ce filet vaut pour tout ce qui viendrait d'ailleurs.
    return <span className="font-mono text-[0.9em]">{latex}</span>
  }

  const Balise = bloc ? 'div' : 'span'

  // Le HTML vient de KaTeX à partir d'un LaTeX déjà validé côté serveur
  // (`RichText`), et KaTeX échappe ce qu'il ne comprend pas. C'est le SEUL
  // endroit de l'application où l'on injecte du HTML, et il est encadré des
  // deux côtés.
  return <Balise className={bloc ? 'my-2 overflow-x-auto' : ''} dangerouslySetInnerHTML={{ __html: html }} />
}
